import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import {
  addAdminService,
  updateAdminService,
  getByIdAdminService,
} from '@/services/AdminServices';
import { getRoleService } from '@/services/RoleServices';
import validationErrors from '@/lib/validationErrors';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import { useDispatch } from 'react-redux';
import ToastMe from '@/components/ui/ToastMe';

const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },{ value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const calcRate = (gross) => {
  const g = Number(gross) || 0;
  return {
    monthly: g,
    daily  : +(g / 22).toFixed(2),
    hourly : +(g / (22 * 8)).toFixed(2),
  };
};

const convertFiveDigitToHHMM = (fiveDigitTime) => {
  if (!fiveDigitTime || fiveDigitTime.length !== 5) return fiveDigitTime || '08:00';
  try {
    const hours = fiveDigitTime.substring(0, 2);
    const minutes = fiveDigitTime.substring(2, 4);
    return `${hours}:${minutes}`;
  } catch {
    return '08:00';
  }
};
const ensureHHMMFormat = (time) => (time && time.includes(':')) ? time : convertFiveDigitToHHMM(time);

// ─────────────────────────────────────────────────────────────
// Robust role exclusion (chef, waiter + variants; keep old super_admin filters)
// ─────────────────────────────────────────────────────────────
const EXCLUDE_ROLE_NORMALIZED = new Set([
  'chef',
  'waiter',
  'waitress',
  'super admin',
  'superadmin',
  'adb super admin',
]);

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const shouldExcludeRole = (role) => {
  const n1 = normalize(role?.name);
  const n2 = normalize(role?.value);
  return EXCLUDE_ROLE_NORMALIZED.has(n1) || EXCLUDE_ROLE_NORMALIZED.has(n2);
};

// ─────────────────────────────────────────────────────────────
// Effective branch resolver: LS → Axios header → JWT default
// ─────────────────────────────────────────────────────────────
function getEffectiveBranchId() {
  const isObjectId = (v) => typeof v === 'string' && /^[0-9a-fA-F]{24}$/.test(v);

  try {
    const ls = localStorage.getItem('branch_id');
    if (ls && ls !== 'all' && isObjectId(ls)) return ls;
  } catch {}

  try {
    const hdr = axios?.defaults?.headers?.common?.['X-Branch-Id'];
    if (hdr && hdr !== 'all' && isObjectId(hdr)) return hdr;
  } catch {}

  try {
    const tok =
      localStorage.getItem('customer_jwt') ||
      localStorage.getItem('customerToken') ||
      localStorage.getItem('jwt_token');
    if (tok) {
      const d = jwtDecode(tok) || {};
      const def = d?.branch_id;
      if (isObjectId(def)) return def;
    }
  } catch {}

  return null;
}

const AddEditDrawer = ({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) => {
  const selectRef = useRef(null);
  const dispatch  = useDispatch();

  const {
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '', email: '', phone_no: '',
      status: '1',
      biometricId: '',
      password: '', confirm_password: '',
      role_id: '', salary: '',
      contractStartDate: '', contractEndDate: '',
      holidayDays: [],
      defaultInTime: '08:00', defaultOutTime: '17:00',
      penaltyRate: '',
    },
  });

  const [rolesOptionList, setRolesOptionList] = useState([]);
  const [itemStatus, setItemStatus]           = useState('1');
  const [isLoading, setIsLoading]             = useState(false);
  const [isFormSubmit, setIsFormSubmit]       = useState(false);
  const [salaryRate, setSalaryRate]           = useState({ monthly: '', daily: '', hourly: '' });

  useEffect(() => {
    if (activeDrawer) {
      reset();
      setItemStatus('1');
      setSalaryRate({ monthly: '', daily: '', hourly: '' });
      fetchRoles();
      if (editDataId) getDataById(editDataId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawer]);

  const fetchRoles = () => {
    setIsLoading(true);
    dispatch(getRoleService({ search: '', page: 1, limit: 100, extra_filter: {} }))
      .then((res) => {
        const rows = res?.data?.data?.data || [];
        const cleaned = rows
          .filter((r) => !shouldExcludeRole(r))
          .map((r) => ({ value: r._id, label: r.name }));
        setRolesOptionList(cleaned);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const getDataById = (id) => {
    setIsLoading(true);
    dispatch(getByIdAdminService(id))
      .then((res) => {
        const d = res?.data?.data;
        if (!d) return;

        setValue('name', d.name);
        setValue('email', d.email);
        setValue('phone_no', d.phone_no);
        setValue('status', String(d.status || 1));
        setItemStatus(String(d.status || 1));
        setValue('biometricId', d.biometricId || '');
        setValue('role_id', d?.role_id?._id || d?.role_id || '');
        setValue('salary', d.salary || '');
        setValue('contractStartDate', d.contractStartDate ? new Date(d.contractStartDate).toISOString().split('T')[0] : '');
        setValue('contractEndDate',   d.contractEndDate   ? new Date(d.contractEndDate).toISOString().split('T')[0] : '');
        setValue('holidayDays', Array.isArray(d.holidayDays) ? d.holidayDays : []);

        setValue('defaultInTime', ensureHHMMFormat(d.defaultInTime));
        setValue('defaultOutTime', ensureHHMMFormat(d.defaultOutTime));
        setValue('penaltyRate', d.penaltyRate || '');

        setSalaryRate(
          d.salaryRate
            ? { monthly: d.salaryRate.monthly, daily: d.salaryRate.daily, hourly: d.salaryRate.hourly }
            : calcRate(d.salary)
        );
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const handleDrawerClose = () => {
    reset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  const handleChangeStatus = (e) => {
    setValue('status', e.target.value);
    setItemStatus(e.target.value);
  };

  const onSubmit = (data) => {
    if (data.confirm_password !== data.password) {
      ToastMe('Password and confirm password do not match', 'error');
      return;
    }
    setIsFormSubmit(true);

    data.isSuperAdmin = 0;
    data.contractStartDate = data.contractStartDate || null;
    data.contractEndDate = data.contractEndDate || null;
    data.holidayDays = Array.isArray(data.holidayDays) ? data.holidayDays : [];

    data.defaultInTime = data.defaultInTime || '08:00';
    data.defaultOutTime = data.defaultOutTime || '17:00';

    const effectiveBranch = getEffectiveBranchId();
    if (effectiveBranch) {
      data.branch_ids = [effectiveBranch];
      data.default_branch_id = effectiveBranch;
      console.log('🔄 Using effective branch for employee creation and enrollment:', effectiveBranch);
    } else {
      console.warn('❌ No effective branch found (LS/header/JWT).');
      ToastMe('Select a branch (top bar) before creating an employee.', 'warning');
      setIsFormSubmit(false);
      return; // Stop submission if no branch
    }

    const svc = editDataId ? updateAdminService(editDataId, data) : addAdminService(data);

    dispatch(svc)
      .then((res) => {
        if (!editDataId) {
          const biometricId = res?.data?.data?.biometricId;
          const branchName = localStorage.getItem('branch_name') || effectiveBranch;
          ToastMe(`Employee created in branch ${branchName}. Biometric ID: ${biometricId ?? '—'}`, 'success');
        } else {
          ToastMe('Employee updated successfully', 'success');
        }
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        console.error('❌ Employee create/update error:', errorData || message);
        
        // Check if it's a device enrollment error
        if (message?.includes('No HRM devices found') || message?.includes('Enrollment failed')) {
          ToastMe(`Employee created but device enrollment failed: ${message}`, 'warning');
          handleDrawerClose(); // Still close the drawer since employee was created
        } else {
          validationErrors(errorData, message);
        }
      })
      .finally(() => setIsFormSubmit(false));
  };

  const selectPortalProps = {
    menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="name"
            rules={{ required: true, minLength: 2, maxLength: 50 }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required"
                size="sm" labelText="Name" type="text"
                errorData={{ minLength: '2', maxLength: '50' }}
                errorType={errors?.name?.type}
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required"
                size="sm" labelText="Email" type="text"
                errorData={{ minLength: '6', maxLength: '50' }}
                errorType={errors?.email?.type}
              />
            )}
          />

          <Controller
            control={control}
            name="phone_no"
            rules={{ required: true, minLength: 7, maxLength: 20 }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required"
                size="sm" labelText="Phone" type="text"
                errorData={{ minLength: '7', maxLength: '20' }}
                errorType={errors?.phone_no?.type}
              />
            )}
          />

          <div className="form-input-item">
            <label className="db-field-title required">Status</label>
            <div className="db-field-radio-group">
              <input type="radio" id="active" name="status" value="1" checked={itemStatus === '1'} onChange={handleChangeStatus} />
              <label htmlFor="active">Active</label>

              <input type="radio" id="inactive" name="status" value="2" checked={itemStatus === '2'} onChange={handleChangeStatus} />
              <label htmlFor="inactive">Inactive</label>
            </div>
          </div>

          <Controller
            control={control}
            name="password"
            rules={editDataId ? { minLength: 6, maxLength: 50 } : { required: true, minLength: 6, maxLength: 50 }}
            render={({ field }) => (
              <Input {...field}
                labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                type="password" labelText="New Password" autoComplete="off"
                errorData={{ minLength: '6', maxLength: '50' }}
                errorType={errors?.password?.type}
              />
            )}
          />

          <Controller
            control={control}
            name="confirm_password"
            rules={editDataId ? { minLength: 6, maxLength: 50 } : { required: true, minLength: 6, maxLength: 50 }}
            render={({ field }) => (
              <Input {...field}
                labelStyleClassName={editDataId ? '' : 'db-field-title required'}
                type="password" labelText="Confirm Password" autoComplete="off"
                errorData={{ minLength: '6', maxLength: '50' }}
                errorType={errors?.confirm_password?.type}
              />
            )}
          />

          <Controller
            control={control}
            name="role_id"
            rules={{ required: true }}
            render={({ field }) => (
              <InputSelect
                ref={selectRef}
                id="role_id"
                /* prevent portal/menu reuse across drawers */
                instanceId={`role-select-employee-${activeDrawer ? 'open' : 'closed'}`}
                key={`employee-role-${rolesOptionList.length}-${field.value || 'none'}`}
                labelText="Role"
                options={rolesOptionList}
                value={rolesOptionList.find((o) => o.value === field.value) || null}
                onChange={(opt) => field.onChange(opt ? opt.value : '')}
                errorType={errors?.role_id?.type}
                {...selectPortalProps}
              />
            )}
          />

          <Controller
            control={control}
            name="biometricId"
            rules={{ required: false }}
            render={({ field }) => (
              <Input {...field} labelText="Biometric ID" labelStyleClassName="db-field-title" size="sm" type="text" />
            )}
          />

          <Controller
            control={control}
            name="salary"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                onChange={(e) => { field.onChange(e); setSalaryRate(calcRate(e.target.value)); }}
                labelStyleClassName="db-field-title required"
                size="sm" labelText="Salary"
                type="number"
                errorData={{ required: 'Salary is required' }}
                errorType={errors?.salary?.type}
              />
            )}
          />

          <Input readOnly value={salaryRate.monthly} labelStyleClassName="db-field-title" size="sm" labelText="Monthly (read-only)" type="number" />
          <Input readOnly value={salaryRate.daily}   labelStyleClassName="db-field-title" size="sm" labelText="Daily (read-only)"   type="number" />
          <Input readOnly value={salaryRate.hourly}  labelStyleClassName="db-field-title" size="sm" labelText="Hourly (read-only)"  type="number" />

          <Controller
            control={control}
            name="contractStartDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Contract Start Date" type="date" errorData={{ required: 'Required' }} errorType={errors?.contractStartDate?.type} />
            )}
          />
          <Controller
            control={control}
            name="contractEndDate"
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title required" size="sm" labelText="Contract End Date" type="date" errorData={{ required: 'Required' }} errorType={errors?.contractEndDate?.type} />
            )}
          />

          <Controller
            control={control}
            name="holidayDays"
            render={({ field }) => {
              const val = (field.value || []).map(
                (n) => dayOfWeekOptions.find((o) => o.value === n) || { value: n, label: `Day ${n}` }
              );
              return (
                <Select
                  isMulti
                  placeholder="Select Holiday Days"
                  value={val}
                  onChange={(opts) => field.onChange(opts ? opts.map((o) => o.value) : [])}
                  options={dayOfWeekOptions}
                  menuPortalTarget={selectPortalProps.menuPortalTarget}
                  styles={selectPortalProps.styles}
                />
              );
            }}
          />

          <Controller control={control} name="defaultInTime"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Default In Time (HH:mm)" type="time" />
            )}
          />
          <Controller control={control} name="defaultOutTime"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Default Out Time (HH:mm)" type="time" />
            )}
          />

          <Controller control={control} name="penaltyRate"
            render={({ field }) => (
              <Input {...field} labelStyleClassName="db-field-title" size="sm" labelText="Penalty Rate (%)" type="number" />
            )}
          />
        </div>

        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="submit" className="db-btn py-2 text-white bg-primary" disabled={isFormSubmit || isLoading}>
              <i className="lab lab-save" /><span>Save</span>
              {isFormSubmit && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
            </button>
            <button type="button" className="modal-btn-outline modal-close" onClick={handleDrawerClose}>
              <i className="lab lab-close" /><span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;